import clsx from 'clsx';
import { useRef, useState } from 'react';
import { DropZoneArea } from '@/app/components/DropZoneArea';
import { BaseButton } from '@/app/components/buttons/BaseButton';
import { useFileDrop } from '@/hooks/useFileDrop';
import { isFileSizeValid } from '@/hooks/useFileDrop/utils/validations';
import { SharePdfFileCard } from './SharePdfFileCard';
import FileIcon from '/public/icons/file.svg';
import UploadIcon from '/public/icons/upload-cloud.svg';

const PDF_EXTENSIONS = ['pdf'];
const MAX_PDF_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
const MAX_PDF_FILES_COUNT = 5;
const MAX_PDF_TOTAL_SIZE = 15 * 1024 * 1024; // 15 MB

interface SharePdfDropZoneProps {
  files: File[];
  onChange: (files: File[]) => void;
  disabled?: boolean;
  uploadError?: boolean;
}

type ValidationError = 'extension' | 'fileSize' | 'totalSize' | 'maxCount' | 'uploadFailed' | null;

const ERROR_MESSAGES: Record<NonNullable<ValidationError>, string> = {
  extension: 'Please upload PDF files only.',
  fileSize: `File is too large. Maximum size is ${MAX_PDF_FILE_SIZE / 1024 / 1024} MB per file.`,
  totalSize: `Total attachment size cannot exceed ${MAX_PDF_TOTAL_SIZE / 1024 / 1024} MB.`,
  maxCount: `Maximum ${MAX_PDF_FILES_COUNT} files can be uploaded.`,
  uploadFailed: 'Upload failed. Please try again.',
};

export function SharePdfDropZone({ files, onChange, disabled, uploadError }: SharePdfDropZoneProps) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [localError, setLocalError] = useState<ValidationError>(null);

  const isMaxCountReached = files.length >= MAX_PDF_FILES_COUNT;

  const onSelectFiles = (newFiles: File[]) => {
    if (newFiles.length > MAX_PDF_FILES_COUNT) {
      setLocalError('maxCount');
      return;
    }
    const totalSize = newFiles.reduce((sum, f) => sum + f.size, 0);
    if (totalSize > MAX_PDF_TOTAL_SIZE) {
      setLocalError('totalSize');
      return;
    }
    setLocalError(null);
    onChange(newFiles);
  };

  const { isDragging, inputRefProps, dropZoneProps, isAllowedExtensionError, isMaxFileSizeError } = useFileDrop({
    setFiles: onSelectFiles,
    files,
    allowedExtensions: PDF_EXTENSIONS,
    maxFileSize: MAX_PDF_FILE_SIZE,
    maxFilesCount: Math.max(MAX_PDF_FILES_COUNT - files.length, 1),
    isMultipleFiles: true,
  });

  // Wrap input onChange to catch count overflow before useFileDrop handles it
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files ?? []);
    if (files.length + selected.length > MAX_PDF_FILES_COUNT) {
      setLocalError('maxCount');
      e.target.value = '';
      return;
    }
    inputRefProps.onChange(e);
  };

  const hasOversizedFile = files.some((f) => !isFileSizeValid(f, MAX_PDF_FILE_SIZE));

  // Determine which error to show (priority: extension > fileSize > count > totalSize > uploadFailed)
  let validationError: ValidationError = null;
  if (isAllowedExtensionError) validationError = 'extension';
  else if (isMaxFileSizeError || hasOversizedFile) validationError = 'fileSize';
  else if (localError) validationError = localError;
  else if (uploadError) validationError = 'uploadFailed';

  const isError = validationError !== null;

  const onUploadClick = () => {
    if (isMaxCountReached) {
      setLocalError('maxCount');
      return;
    }
    fileInputRef.current?.click();
  };

  const onDeleteFile = (fileToDelete: File) => {
    setLocalError(null);
    onChange(files.filter((f) => f !== fileToDelete));
  };

  return (
    <div>
      <DropZoneArea
        {...dropZoneProps}
        uploadIcon={FileIcon}
        isDragging={isDragging}
        isError={isError}
        disabled={disabled}
        className={clsx('px-[30px] py-[28px]', {
          'bg-red-50': isError,
        })}
      >
        <div className="flex flex-col gap-[8px] items-center justify-center">
          <p className="text-button-medium text-black mt-3 text-center">Drag your file(s) to start uploading</p>
          <div className="flex flex-row gap-[12px] items-center justify-center">
            <span className="w-[60px] h-[1px] bg-grey-400" />
            <span className="text-body-small text-neutral-500">or</span>
            <span className="w-[60px] h-[1px] bg-grey-400" />
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf"
            style={{ display: 'none' }}
            disabled={disabled}
            {...inputRefProps}
            onChange={handleInputChange}
          />
          <BaseButton
            startIcon={<UploadIcon className="w-4 h-4 text-blue-300 fill-current" />}
            intent="ghost"
            size="medium"
            type="button"
            onClick={onUploadClick}
            disabled={disabled}
          >
            Upload Files
          </BaseButton>
        </div>
        <p className="text-body-small text-neutral-500 mt-[14px] text-center">
          PDF only · Max {MAX_PDF_FILES_COUNT} files · Max {MAX_PDF_FILE_SIZE / 1024 / 1024} MB per file · Max{' '}
          {MAX_PDF_TOTAL_SIZE / 1024 / 1024} MB total
        </p>
        {validationError && (
          <p className="text-body-small text-red-500 mt-[6px] text-center">{ERROR_MESSAGES[validationError]}</p>
        )}
      </DropZoneArea>

      {files.length > 0 && (
        <div className="mt-4 grid grid-cols-3 gap-2 sm:grid-cols-5 sm:gap-x-1.5">
          {files.map((file, index) => (
            <SharePdfFileCard key={`${file.name}-${file.size}-${index}`} file={file} onDelete={onDeleteFile} />
          ))}
        </div>
      )}
    </div>
  );
}
